import { InputMaybe, MagicTokenPermissions } from '@graphql/generated';
import { createTelemetry, createTelemetryObject } from '@shared/core/analytics/telemetry';

export const category = 'adminWebsiteOverview';
const pagePrefix = 'admin';
const action = 'WebsiteOverviewButtonInteracted';
const photosAction = 'PhotosButtonInteracted';

export const getPhotoDownloadTelemetryActions = (baseAction: string, baseCategory: string) => ({
  photoDownloadFailed: (eventId: string, collectionId: string, photoUrl: string) => ({
    action: baseAction,
    category: baseCategory,
    extraInfo: {
      name: 'photoDownloadFailed',
      eventId,
      collectionId,
      photoUrl,
    },
  }),
  multiplePhotoDownloadFailed: (eventId: string, collectionId: string, photoUrls: string[]) => ({
    action: baseAction,
    category: baseCategory,
    extraInfo: {
      name: 'multiplePhotoDownloadFailed',
      eventId,
      collectionId,
      photoUrls,
    },
  }),
  photoZipDownloadFailed: (eventId: string, collectionId: string, photoUrls: string[]) => ({
    action: baseAction,
    category: baseCategory,
    extraInfo: {
      name: 'photoZipDownloadFailed',
      eventId,
      collectionId,
      photoUrls,
    },
  }),
});

const telemObject = createTelemetryObject({
  pages: {
    website: () => ({
      category: 'website',
      pagePrefix,
      page: 'overview',
    }),
    photos: () => ({
      category: 'website',
      pagePrefix,
      page: 'photos',
    }),
  },
  actions: {
    clickHeaderLink: (link: string, telemetryCategory: string) => ({
      action: 'HeaderLinkInteracted',
      category: telemetryCategory,
      extraInfo: {
        link,
      },
    }),
    clickWebsitePreview: () => ({
      action: 'WebsitePreviewInteracted',
      category,
    }),
    clickWebsiteDesign: () => ({
      action: 'WebsiteDesignInteracted',
      category,
    }),
    eventURLCopied: (eventId: string, eventURL: string) => ({
      action,
      category,
      extraInfo: {
        name: 'eventURLCopied',
        eventId,
        eventURL,
      },
    }),
    eventURLEditClicked: (eventId: string) => ({
      action,
      category,
      extraInfo: {
        name: 'eventURLEditClicked',
        eventId,
      },
    }),
    rsvpCTAClicked: (eventId: string) => ({
      action,
      category,
      extraInfo: {
        name: 'rsvpButtonClicked',
        eventId,
      },
    }),
    eventURLChanged: (eventId: string, nextValue: string, originalValue: string, persistedValue: string) => ({
      action: 'HandleChanged',
      category,
      extraInfo: {
        name: 'handleChanged',
        eventId,
        nextValue,
        originalValue,
        persistedValue,
      },
    }),
    websitePreviewCTAInteraction: (eventId: string, name: string) => ({
      action,
      category,
      extraInfo: {
        name,
        eventId,
      },
    }),
    suggestionCardClicked: (eventId: string, label: string) => ({
      action,
      category,
      extraInfo: {
        name: 'getStartedCardClicked',
        eventId,
        label,
      },
    }),

    addNewAlbumCTAClick: (eventId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'addNewAlbumButtonClick',
        eventId,
      },
    }),
    createNewAlbumSuccess: (eventId: string, albumName: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'createNewAlbumSuccess',
        eventId,
        albumName,
      },
    }),
    viewAlbumClicked: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'viewAlbumClicked',
        eventId,
        albumId,
      },
    }),
    deleteAlbumClicked: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'deleteAlbumClicked',
        eventId,
        albumId,
      },
    }),
    photosBreadcrumbNavigation: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'photosBreadcrumbNavigation',
        eventId,
        albumId,
      },
    }),
    viewAlbumPhotoClicked: (eventId: string, albumId: string, mediaId: string, photoUrl: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'viewAlbumPhotoClicked',
        eventId,
        albumId,
        mediaId,
        photoUrl,
      },
    }),
    deleteAlbumPhotoClicked: (eventId: string, albumId: string, mediaId: string, photoUrl: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'deleteAlbumPhotoClicked',
        eventId,
        albumId,
        mediaId,
        photoUrl,
      },
    }),
    deleteMediaClicked: (eventId: string, mediaId: string, photoUrl: string, albumId?: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'deleteMediaClicked',
        eventId,
        mediaId,
        photoUrl,
        albumId,
      },
    }),
    uploadAlbumPhotoCTAClicked: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'uploadAlbumPhotoButtonClicked',
        eventId,
        albumId,
      },
    }),
    openFilePickerClicked: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'openFilePicker',
        eventId,
        albumId,
      },
    }),
    uploadAlbumPhoto: (eventId: string, albumId: string, mediaId: string, photoId: string, photoUrl: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'uploadAlbumPhoto',
        eventId,
        albumId,
        mediaId,
        photoId,
        photoUrl,
      },
    }),
    editAlbumSettingsCTAClicked: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'editAlbumSettingsButtonClicked',
        eventId,
        albumId,
      },
    }),
    updatedAlbumSettings: (eventId: string, albumId: string, extraInfo: object) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'updatedAlbumSettings',
        eventId,
        albumId,
        ...extraInfo,
      },
    }),
    toggleMagicLink: (eventId: string, albumId: string, isActive: boolean) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'magicLinkUpdated',
        eventId,
        albumId,
        isActive,
      },
    }),
    magicLinkCopied: (eventId: string, albumId: string, magicLink: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'magicLinkCopied',
        eventId,
        albumId,
        magicLink,
      },
    }),
    faqClicked: (eventId: string, question: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'faqClicked',
        eventId,
        question,
      },
    }),
    tokenPermissionUpdated: (eventId: string, albumId: string, permissions: InputMaybe<MagicTokenPermissions>[]) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'tokenPermissionUpdated',
        eventId,
        albumId,
        permissions,
      },
    }),
    selectAllPhotosCTAClicked: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'selectAllPhotosButtonClicked',
        eventId,
        albumId,
      },
    }),
    deselectAllPhotosCTAClicked: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'deselectAllPhotosButtonClicked',
        eventId,
        albumId,
      },
    }),
    addMediaFromMediaLibrary: (eventId: string, albumId: string, mediaIds: string[]) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'addMediaFromMediaLibrary',
        eventId,
        albumId,
        mediaIds,
      },
    }),
    selectPhotosCTAClicked: (eventId: string, albumId: string) => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'selectPhotosButtonClicked',
        eventId,
      },
    }),
    infoLinkClicked: (linkTo: 'moments' | 'websiteSettings') => ({
      action: photosAction,
      category,
      extraInfo: {
        name: 'infoLinkClicked',
        lookingFor: linkTo,
      },
    }),
    ...getPhotoDownloadTelemetryActions(photosAction, category),
  },
});

const { TelemetryProvider: WebsiteOverviewTelemetryProvider, useTelemetry: useWebsiteOverviewTelemetry } = createTelemetry(telemObject);

export { WebsiteOverviewTelemetryProvider, useWebsiteOverviewTelemetry };
